import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import Footer from '../components/common/Footer';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    // You can integrate with your backend API here
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className='min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-6xl mx-auto'>
        {/* Header */}
        <div className='mb-8'>
          <Link
            to='/home'
            className='inline-flex items-center text-blue-600 hover:text-blue-800 mb-4'
          >
            <ArrowLeft className='h-4 w-4 mr-2' />
            Back to Home
          </Link>
          <h1 className='text-3xl font-bold text-gray-900 mb-2'>Contact Us</h1>
          <p className='text-gray-600'>
            Get in touch with our team - we're here to help!
          </p>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
          {/* Contact Information */}
          <div className='space-y-6'>
            {/* Contact Methods */}
            <div className='bg-white rounded-lg shadow-sm p-6'>
              <h2 className='text-2xl font-semibold text-gray-900 mb-6'>
                Get in Touch
              </h2>

              <div className='space-y-4'>
                <div className='flex items-start'>
                  <Mail className='h-6 w-6 text-blue-600 mt-1 mr-3 flex-shrink-0' />
                  <div>
                    <h3 className='font-medium text-gray-900'>Email Support</h3>
                    <p className='text-gray-600 mt-1'>
                      <a
                        href='mailto:support@pcsoe-lotto.com'
                        className='text-blue-600 hover:text-blue-800 underline'
                      >
                        support@pcsoe-lotto.com
                      </a>
                    </p>
                    <p className='text-sm text-gray-500 mt-1'>
                      For general inquiries and support
                    </p>
                  </div>
                </div>

                <div className='flex items-start'>
                  <Phone className='h-6 w-6 text-blue-600 mt-1 mr-3 flex-shrink-0' />
                  <div>
                    <h3 className='font-medium text-gray-900'>Phone Support</h3>
                    <p className='text-gray-600 mt-1'>
                      <a
                        href='tel:+632-8523-8888'
                        className='text-blue-600 hover:text-blue-800 underline'
                      >
                        +63 (2) 8523-8888
                      </a>
                    </p>
                    <p className='text-sm text-gray-500 mt-1'>
                      Available 24/7 for urgent matters
                    </p>
                  </div>
                </div>

                <div className='flex items-start'>
                  <MapPin className='h-6 w-6 text-blue-600 mt-1 mr-3 flex-shrink-0' />
                  <div>
                    <h3 className='font-medium text-gray-900'>
                      Office Address
                    </h3>
                    <p className='text-gray-600 mt-1'>
                      PCSO Main Office
                      <br />
                      E. Rodriguez Jr. Ave, Quezon City
                      <br />
                      Metro Manila, Philippines
                    </p>
                  </div>
                </div>

                <div className='flex items-start'>
                  <Clock className='h-6 w-6 text-blue-600 mt-1 mr-3 flex-shrink-0' />
                  <div>
                    <h3 className='font-medium text-gray-900'>Support Hours</h3>
                    <p className='text-gray-600 mt-1'>
                      Monday - Sunday: 24/7
                      <br />
                      Online Support: Always Available
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className='bg-white rounded-lg shadow-sm p-6'>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Frequently Asked Questions
              </h2>

              <div className='space-y-4'>
                <div>
                  <h3 className='font-medium text-gray-900 mb-2'>
                    How do I create an account?
                  </h3>
                  <p className='text-gray-600 text-sm'>
                    Click on the "Register" button on our homepage and fill out
                    the required information. You must be at least 18 years old
                    to create an account.
                  </p>
                </div>

                <div>
                  <h3 className='font-medium text-gray-900 mb-2'>
                    How do I check my lottery results?
                  </h3>
                  <p className='text-gray-600 text-sm'>
                    Log into your account and navigate to "Game Results" to view
                    all recent draw results and check if your tickets are
                    winners.
                  </p>
                </div>

                <div>
                  <h3 className='font-medium text-gray-900 mb-2'>
                    What payment methods do you accept?
                  </h3>
                  <p className='text-gray-600 text-sm'>
                    We accept various payment methods including bank transfers,
                    e-wallets, and other secure payment options. Check your
                    account for available methods.
                  </p>
                </div>

                <div>
                  <h3 className='font-medium text-gray-900 mb-2'>
                    How long does it take to process withdrawals?
                  </h3>
                  <p className='text-gray-600 text-sm'>
                    Withdrawal processing typically takes 1-3 business days,
                    depending on your chosen payment method and verification
                    requirements.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className='bg-white rounded-lg shadow-sm p-6'>
            <h2 className='text-2xl font-semibold text-gray-900 mb-6'>
              Send us a Message
            </h2>

            <form onSubmit={handleSubmit} className='space-y-6'>
              <div>
                <label
                  htmlFor='name'
                  className='block text-sm font-medium text-gray-700 mb-2'
                >
                  Full Name *
                </label>
                <input
                  type='text'
                  id='name'
                  name='name'
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                  placeholder='Enter your full name'
                />
              </div>

              <div>
                <label
                  htmlFor='email'
                  className='block text-sm font-medium text-gray-700 mb-2'
                >
                  Email Address *
                </label>
                <input
                  type='email'
                  id='email'
                  name='email'
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                  placeholder='Enter your email address'
                />
              </div>

              <div>
                <label
                  htmlFor='subject'
                  className='block text-sm font-medium text-gray-700 mb-2'
                >
                  Subject *
                </label>
                <select
                  id='subject'
                  name='subject'
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                >
                  <option value=''>Select a subject</option>
                  <option value='account'>Account Issues</option>
                  <option value='payment'>Payment & Withdrawal</option>
                  <option value='technical'>Technical Support</option>
                  <option value='game'>Game Questions</option>
                  <option value='complaint'>Complaint</option>
                  <option value='suggestion'>Suggestion</option>
                  <option value='other'>Other</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor='message'
                  className='block text-sm font-medium text-gray-700 mb-2'
                >
                  Message *
                </label>
                <textarea
                  id='message'
                  name='message'
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                  placeholder='Please describe your inquiry or issue in detail...'
                ></textarea>
              </div>

              <button
                type='submit'
                className='w-full flex items-center justify-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              >
                <Send className='h-5 w-5 mr-2' />
                Send Message
              </button>
            </form>

            <div className='mt-6 p-4 bg-blue-50 rounded-md'>
              <p className='text-sm text-blue-800'>
                <strong>Note:</strong> For urgent account or security issues,
                please call our 24/7 phone support line. We typically respond to
                email inquiries within 24 hours.
              </p>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className='mt-8 bg-white rounded-lg shadow-sm p-6'>
          <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
            Additional Resources
          </h2>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
            <div className='text-center'>
              <h3 className='font-medium text-gray-900 mb-2'>Help Center</h3>
              <p className='text-sm text-gray-600 mb-3'>
                Browse our comprehensive help articles and tutorials
              </p>
              <Link
                to='/help'
                className='text-blue-600 hover:text-blue-800 underline text-sm'
              >
                Visit Help Center
              </Link>
            </div>
            <div className='text-center'>
              <h3 className='font-medium text-gray-900 mb-2'>Live Chat</h3>
              <p className='text-sm text-gray-600 mb-3'>
                Chat with our support team in real-time
              </p>
              <button className='text-blue-600 hover:text-blue-800 underline text-sm'>
                Start Live Chat
              </button>
            </div>
            <div className='text-center'>
              <h3 className='font-medium text-gray-900 mb-2'>
                Community Forum
              </h3>
              <p className='text-sm text-gray-600 mb-3'>
                Connect with other players and share experiences
              </p>
              <Link
                to='/forum'
                className='text-blue-600 hover:text-blue-800 underline text-sm'
              >
                Join Community
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;
